import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { sleep } from '@shared/helpers/promise.helpers';
import { isDefined } from '@shared/helpers/array.helpers';

/**
 * A single item in the queue. Keeps track of the resolve and reject functions.
 */
interface QueueItem<ResponseDataType = any, RequestDataType = any> {
  config: AxiosRequestConfig<RequestDataType>;

  resolve: (value: AxiosResponse<ResponseDataType>) => void;
  reject: (reason: any) => void;
}

/**
 * Options for the AxiosRequestQueue.
 */
interface AxiosQueueOptions {
  /**
   * Axios config to use for all requests. Can be overridden by the config of the request.
   */
  axiosConfig?: AxiosRequestConfig;

  /**
   * The delay between requests in the queue. Will always wait at least 0ms to let resole function to resolve first.
   */
  queueDelay?: number;
}

export class AxiosRequestQueue<ResponseDataType = any, RequestDataType = any> {
  private queue: QueueItem<ResponseDataType, RequestDataType>[] = [];
  private processingItem?: QueueItem<ResponseDataType, RequestDataType>;
  private idleWaiters: Array<() => void> = [];
  private axiosConfig: AxiosRequestConfig<RequestDataType>;
  private queueDelay: number;

  constructor({ axiosConfig = {}, queueDelay = 0 }: AxiosQueueOptions = {}) {
    this.axiosConfig = axiosConfig;
    this.queueDelay = queueDelay;
  }

  /**
   * Makes a request to the API. Will merge the axios config with the config of the request.
   * @param config The config for the request.
   * @returns The response from the API.
   */
  private async request(
    config: AxiosRequestConfig<RequestDataType>,
  ): Promise<AxiosResponse<ResponseDataType>> {
    return axios({
      ...this.axiosConfig,
      ...config,
    });
  }

  /**
   * Processes next item in the queue if not already processing.
   */
  private async processQueue(): Promise<void> {
    const [item, ...restQueue] = this.queue;

    if (this.processingItem || item === undefined) return;

    this.processingItem = item; // Store current item to be able to reject it if queue is cleared.
    this.queue = restQueue;

    try {
      const response = await this.request(item.config);
      item.resolve(response);
    } catch (error) {
      item.reject(error);
    }

    // Let the PUT's await continuation (store `updated_at`) run first.
    await Promise.resolve();
    this.processingItem = undefined;
    this.notifyIdle();

    // Always wait at least 0ms to let resole function to resolve first
    await sleep(this.queueDelay);
    this.processQueue();
  }

  /**
   * Resolve waiters when nothing is in flight or queued.
   */
  private notifyIdle(): void {
    if (this.processingItem || this.queue.length > 0) return;
    const waiters = this.idleWaiters;
    this.idleWaiters = [];
    for (const resolve of waiters) resolve();
  }

  /**
   * Enqueues a request.
   * @param config The config for the request.
   * @returns The promise for the request.
   */
  public enqueue(
    config: AxiosRequestConfig<RequestDataType>,
  ): Promise<AxiosResponse<ResponseDataType>> {
    return new Promise((resolve, reject) => {
      this.queue.push({
        config,
        resolve,
        reject,
      });
      void this.processQueue();
    });
  }

  /**
   * Enqueues a PUT request. Almost the same as enqueue, but no need to pass a config object.
   * @param data The data for the request.
   * @param url The url for the request. Can also be specified in global axios config.
   * @param config Optional config for the request.
   * @returns The promise for the request.
   */
  public put(
    data: RequestDataType,
    url?: string,
    config?: AxiosRequestConfig<RequestDataType>,
  ): Promise<AxiosResponse<ResponseDataType>> {
    return this.enqueue({
      ...config,
      method: 'PUT',
      data,
      url: url ?? config?.url ?? this.axiosConfig.url,
    });
  }

  /**
   * Clears the queue.
   * @param rejectPendingRequests If true, the pending requests will be rejected.
   */
  public clearQueue(): void {
    // Reject all pending requests (can't leave awaits hanging)
    [this.processingItem, ...this.queue].forEach((item) => {
      item?.reject(new Error('Queue cleared'));
    });
    this.processingItem = undefined;
    this.queue = [];
    this.notifyIdle();
  }

  /**
   * Resolve when the queue has no in-flight or queued request.
   * Waiters run after the current PUT's resolve callback so
   * `updated_at` bumps land before the next persist reads the store.
   */
  public waitUntilIdle(): Promise<void> {
    if (!this.isLoading()) return Promise.resolve();
    return new Promise((resolve) => {
      this.idleWaiters.push(resolve);
    });
  }

  /**
   * Returns the length of the queue.
   * @returns The length of the queue.
   */
  public getQueueLength(): number {
    return this.queue.length;
  }

  /**
   * Returns true if the queue is loading.
   * @returns True if the queue is loading.
   */
  public isLoading(): boolean {
    return Boolean(this.processingItem) || this.getQueueLength() > 0;
  }

  /**
   * True when another request is waiting behind the in-flight one.
   * A PUT's `await` continuation runs while {@link processingItem} is still set
   * (so {@link waitUntilIdle} sees `updated_at` first). Store spinner flags must
   * use this instead of {@link isLoading}, or they stay true after the last PUT.
   */
  public hasQueuedItems(): boolean {
    return this.getQueueLength() > 0;
  }

  /**
   * Returns the data of the requests in the queue.
   * @returns The data of the requests in the queue.
   */
  public getQueueData(): RequestDataType[] {
    return this.queue.map((item) => item.config.data).filter(isDefined);
  }
}
